import React, { useState, useEffect } from 'react';
import { getCategories, getMealsByCategory } from '../utils/api';
import RecipeCard from '../components/RecipeCard';
import RecipeModal from '../components/RecipeModal';
import { Grid3X3, ArrowLeft, Loader2 } from 'lucide-react';

function Categories({ onFavoriteChange }) {
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryRecipes, setCategoryRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [recipesLoading, setRecipesLoading] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState(null);

    useEffect(() => {
        const fetchCategories = async () => {
            setLoading(true);
            try {
                const categoriesData = await getCategories();
                setCategories(categoriesData);
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    const handleCategoryClick = async (category) => {
        setSelectedCategory(category);
        setRecipesLoading(true);

        try {
            const recipes = await getMealsByCategory(category.strCategory);
            setCategoryRecipes(recipes);
        } catch (error) {
            console.error('Error fetching category recipes:', error);
            setCategoryRecipes([]);
        } finally {
            setRecipesLoading(false);
        }
    };

    const handleBackToCategories = () => {
        setSelectedCategory(null);
        setCategoryRecipes([]);
    };

    const handleViewDetails = (recipe) => {
        setSelectedRecipe(recipe);
    };

    const handleCloseModal = () => {
        setSelectedRecipe(null);
    };

    if (loading) {
        return (
            <div className="categories">
                <div className="container">
                    <div className="loading">
                        <Loader2 className="loading-icon" />
                        <p>Loading categories...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (selectedCategory) {
        return (
            <div className="categories">
                <div className="container">
                    <div className="category-header">
                        <button
                            className="back-btn"
                            onClick={handleBackToCategories}
                            aria-label="Back to categories"
                        >
                            <ArrowLeft size={20} />
                            Back
                        </button>
                        <div className="category-info">
                            <h1>{selectedCategory.strCategory} Recipes</h1>
                            {selectedCategory.strCategoryDescription && (
                                <p className="category-description">
                                    {selectedCategory.strCategoryDescription}
                                </p>
                            )}
                        </div>
                    </div>

                    {recipesLoading ? (
                        <div className="loading">
                            <Loader2 className="loading-icon" />
                            <p>Loading {selectedCategory.strCategory.toLowerCase()} recipes...</p>
                        </div>
                    ) : categoryRecipes.length > 0 ? (
                        <div className="recipes-grid">
                            {categoryRecipes.map(recipe => (
                                <RecipeCard
                                    key={recipe.idMeal}
                                    recipe={recipe}
                                    onViewDetails={handleViewDetails}
                                    onFavoriteChange={onFavoriteChange}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="no-recipes">
                            <p>No recipes found in this category.</p>
                        </div>
                    )}
                </div>

                {selectedRecipe && (
                    <RecipeModal
                        recipe={selectedRecipe}
                        onClose={handleCloseModal}
                        onFavoriteChange={onFavoriteChange}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="categories">
            <div className="container">
                <div className="page-header">
                    <Grid3X3 className="page-icon" />
                    <h1>Recipe Categories</h1>
                    <p>Explore recipes by category</p>
                </div>

                <div className="categories-grid">
                    {categories.map(category => (
                        <div
                            key={category.idCategory}
                            className="category-card"
                            onClick={() => handleCategoryClick(category)}
                        >
                            <div className="category-image">
                                <img
                                    src={category.strCategoryThumb}
                                    alt={category.strCategory}
                                    loading="lazy"
                                />
                            </div>
                            <div className="category-content">
                                <h3 className="category-title">{category.strCategory}</h3>
                                {category.strCategoryDescription && (
                                    <p className="category-preview">
                                        {category.strCategoryDescription.slice(0, 100)}
                                        {category.strCategoryDescription.length > 100 ? '...' : ''}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default Categories;